// OCR 유틸리티 함수
import { createWorker } from 'tesseract.js';

// Worker 인스턴스 캐싱 (성능 최적화)
let workerInstance = null;

/**
 * 이미지 전처리 (밝기, 대비, 선명도 개선)
 * @param {HTMLImageElement|HTMLCanvasElement} image - 원본 이미지
 * @returns {HTMLCanvasElement} 전처리된 Canvas
 */
const preprocessImage = (image) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  canvas.width = image.width || image.naturalWidth;
  canvas.height = image.height || image.naturalHeight;
  
  // 원본 이미지 그리기
  ctx.drawImage(image, 0, 0);
  
  // 이미지 데이터 가져오기
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  
  // 밝기 및 대비 조정
  const brightness = 10; // 밝기 증가
  const contrast = 1.2; // 대비 증가
  
  for (let i = 0; i < data.length; i += 4) {
    // RGB 각 채널에 밝기 및 대비 적용
    for (let j = 0; j < 3; j++) {
      let value = data[i + j];
      
      // 밝기 조정
      value += brightness;
      
      // 대비 조정
      value = ((value - 128) * contrast) + 128;
      
      // 값 범위 제한 (0-255)
      data[i + j] = Math.max(0, Math.min(255, value));
    }
  }
  
  // 전처리된 이미지 데이터 적용
  ctx.putImageData(imageData, 0, 0);
  
  return canvas;
};

/**
 * 이미지를 그레이스케일로 변환 (OCR 정확도 향상)
 * @param {HTMLCanvasElement} canvas - 원본 Canvas
 * @returns {HTMLCanvasElement} 그레이스케일 Canvas
 */
const convertToGrayscale = (canvas) => {
  const ctx = canvas.getContext('2d');
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  
  for (let i = 0; i < data.length; i += 4) {
    // 그레이스케일 값 계산 (가중 평균)
    const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    data[i] = gray;     // R
    data[i + 1] = gray; // G
    data[i + 2] = gray; // B
    // Alpha는 그대로 유지
  }
  
  ctx.putImageData(imageData, 0, 0);
  return canvas;
};

/**
 * Tesseract Worker 초기화 및 반환
 * @returns {Promise<Object>} Tesseract Worker 인스턴스
 */
const getWorker = async () => {
  if (!workerInstance) {
    console.log('🔧 Tesseract Worker 생성 중...');
    // 🔥 미얀마어 우선 지원: mya+eng (미얀마어를 먼저 인식)
    // 미얀마어 문서가 많으므로 미얀마어를 우선 처리
    workerInstance = await createWorker('mya+eng', 1, {
      logger: (m) => {
        if (m.status === 'recognizing text') {
          console.log(`OCR 진행률: ${Math.round(m.progress * 100)}%`);
        } else if (m.status === 'loading tesseract core') {
          console.log('📦 Tesseract 코어 로딩 중...');
        } else if (m.status === 'initializing tesseract') {
          console.log('🔧 Tesseract 초기화 중...');
        } else if (m.status === 'loading language traineddata') {
          console.log('🌐 언어 데이터 로딩 중... (미얀마어 우선)');
        }
      }
    });
    
    // 🔥 미얀마어 최적화 PSM 설정 - 문서 인식 최적화
    await workerInstance.setParameters({
      tessedit_pageseg_mode: '6', // 단일 균일한 텍스트 블록
      // 🔥 미얀마어 문자 범위 확장: \u1000-\u109F (미얀마어 기본), \u1100-\u11FF (미얀마어 확장)
      tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789/-\u1000-\u109F\u1100-\u11FF\uAA60-\uAA7F\u3130-\u318F\uAC00-\uD7AF ',
      tessedit_ocr_engine_mode: '1', // LSTM OCR Engine (더 정확, 미얀마어 지원)
      preserve_interword_spaces: '1', // 단어 간 공백 유지 (미얀마어 중요)
      classify_bln_numeric_mode: '0', // 숫자 모드 비활성화 (혼합 텍스트)
      // 🔥 미얀마어 특화 설정
      tessedit_create_hocr: '0', // HOCR 출력 비활성화 (성능 향상)
      tessedit_create_tsv: '0', // TSV 출력 비활성화 (성능 향상)
      // 미얀마어 단어 분리 개선
      wordrec_enable_assoc: '1', // 단어 연관성 활성화
    });
    
    console.log('✅ Tesseract Worker 준비 완료 (미얀마어 우선 지원)');
  }
  return workerInstance;
};

/**
 * Firebase Storage URL에서 이미지를 Blob으로 다운로드
 * @param {string} imageUrl - 이미지 URL
 * @returns {Promise<Blob>} 다운로드된 이미지 Blob
 */
const downloadImageAsBlob = async (imageUrl) => {
  try {
    console.log('📥 이미지 Blob 다운로드 시작:', imageUrl);
    
    // Firebase Storage URL인 경우, 직접 fetch로 다운로드
    // CORS 문제를 피하기 위해 no-cors 모드 사용 불가 (response를 읽을 수 없음)
    // 대신 서버 사이드에서 프록시를 사용하거나, Firebase Storage의 CORS 설정 필요
    // 하지만 여기서는 직접 fetch 시도
    
    const response = await fetch(imageUrl, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        'Accept': 'image/*'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const blob = await response.blob();
    console.log('✅ 이미지 Blob 다운로드 완료, 크기:', blob.size, 'bytes');
    return blob;
  } catch (error) {
    console.error('❌ 이미지 Blob 다운로드 실패:', error);
    throw error;
  }
};

/**
 * 이미지를 회전시킨 Canvas 요소 생성
 * @param {HTMLImageElement|string} image - 이미지 요소 또는 URL
 * @param {number} rotation - 회전 각도 (0, 90, 180, 270)
 * @returns {Promise<HTMLCanvasElement>} 회전된 Canvas
 */
const rotateImage = async (image, rotation) => {
  return new Promise(async (resolve, reject) => {
    try {
      let img;
      
      // URL인 경우 먼저 이미지로 로드
      if (typeof image === 'string') {
        img = await loadImageFromUrl(image);
      } else {
        img = image;
        // 이미 로드되지 않은 경우 대기
        if (!img.complete) {
          await new Promise((imgResolve, imgReject) => {
            img.onload = () => imgResolve();
            img.onerror = () => imgReject(new Error('이미지 로드 실패'));
          });
        }
      }
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      let width = img.width || img.naturalWidth;
      let height = img.height || img.naturalHeight;
      
      // 회전에 따라 캔버스 크기 조정
      if (rotation === 90 || rotation === 270) {
        canvas.width = height;
        canvas.height = width;
      } else {
        canvas.width = width;
        canvas.height = height;
      }
      
      ctx.save();
      
      // 캔버스 중심으로 이동
      ctx.translate(canvas.width / 2, canvas.height / 2);
      
      // 회전
      ctx.rotate((rotation * Math.PI) / 180);
      
      // 이미지 그리기 (중심 기준)
      ctx.drawImage(img, -width / 2, -height / 2, width, height);
      
      ctx.restore();
      
      resolve(canvas);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Blob을 Image 객체로 변환
 * @param {Blob} blob - 이미지 Blob
 * @returns {Promise<HTMLImageElement>} 로드된 이미지 요소
 */
const blobToImage = async (blob) => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    
    img.onload = () => {
      URL.revokeObjectURL(objectUrl); // 메모리 정리
      console.log('✅ Blob을 Image로 변환 완료:', img.width, 'x', img.height);
      resolve(img);
    };
    
    img.onerror = (error) => {
      URL.revokeObjectURL(objectUrl); // 메모리 정리
      reject(new Error('Blob을 Image로 변환 실패: ' + error));
    };
    
    img.src = objectUrl;
  });
};

/**
 * 여러 각도로 회전된 이미지에서 OCR 수행하여 최적 결과 선택
 * @param {Object} worker - Tesseract Worker
 * @param {HTMLImageElement|string|Blob} imageSource - 이미지 소스
 * @returns {Promise<Object>} 최적 OCR 결과 {text, confidence, rotation}
 */
const recognizeWithRotation = async (worker, imageSource) => {
  const rotations = [0, 90, 180, 270]; // 테스트할 회전 각도
  const results = [];
  
  console.log('🔄 여러 각도로 OCR 시도 중...');
  
  // 이미지 소스 타입에 따라 처리
  let loadedImage = imageSource;
  let imageBlob = null;
  
  if (imageSource instanceof Blob) {
    // Blob인 경우 직접 사용
    imageBlob = imageSource;
    loadedImage = await blobToImage(imageBlob);
  } else if (typeof imageSource === 'string') {
    // URL인 경우 - Firebase Storage URL이면 CORS 문제가 있을 수 있음
    // Tesseract.js는 URL을 직접 처리할 수 있지만, CORS 문제로 실패할 수 있음
    // 따라서 URL을 받았을 때는 에러를 발생시키고, 호출자가 Blob으로 변환하도록 함
    if (imageSource.includes('firebasestorage') || imageSource.includes('firebasestorage.app')) {
      console.log('⚠️ Firebase Storage URL 감지, CORS 문제로 OCR 실패 예상');
      // Firebase Storage URL은 CORS 문제로 직접 fetch가 실패할 수 있음
      // Tesseract.js가 내부적으로 처리하도록 시도하되, 실패 시 에러 처리
      loadedImage = imageSource;
      // CORS 오류가 발생할 것으로 예상되지만, 일단 시도
    } else {
      // 다른 URL인 경우 Blob으로 다운로드 시도
      try {
        imageBlob = await downloadImageAsBlob(imageSource);
        loadedImage = await blobToImage(imageBlob);
      } catch (error) {
        console.warn('⚠️ Blob 다운로드 실패, 직접 URL 사용 시도:', error.message);
        // Blob 다운로드 실패 시 원본 URL 사용
        loadedImage = imageSource;
      }
    }
  }
  // HTMLImageElement인 경우 그대로 사용
  
  for (const rotation of rotations) {
    try {
      console.log(`📐 ${rotation}도 회전 이미지 OCR 시도...`);
      
      let ocrSource = loadedImage;
      
      // 이미지 소스가 URL 문자열인 경우 (Firebase Storage URL)
      if (typeof loadedImage === 'string') {
        // Tesseract.js는 URL을 직접 처리할 수 있지만, CORS 문제가 있을 수 있음
        // 회전이 필요한 경우 먼저 이미지를 로드해야 함
        if (rotation !== 0) {
          try {
            // URL에서 이미지 로드 시도 (CORS 문제가 있을 수 있음)
            // Firebase Storage URL인 경우 CORS 문제로 실패할 수 있음
            if (loadedImage.includes('firebasestorage') || loadedImage.includes('firebasestorage.app')) {
              console.warn(`⚠️ ${rotation}도 회전 시도 중, Firebase Storage URL은 CORS 문제로 실패할 수 있음`);
              // Firebase Storage URL은 CORS 문제로 직접 fetch가 실패할 수 있으므로
              // 회전 없이 원본 URL 사용
              ocrSource = loadedImage;
            } else {
              // 다른 URL인 경우 다운로드 시도
              const img = await blobToImage(await downloadImageAsBlob(loadedImage));
              const canvas = await rotateImage(img, rotation);
              ocrSource = await new Promise((resolve) => {
                canvas.toBlob((blob) => {
                  resolve(blob);
                }, 'image/png');
              });
            }
          } catch (rotateError) {
            console.warn(`⚠️ ${rotation}도 회전 실패 (CORS 문제 가능), 원본 URL 사용:`, rotateError.message);
            // 회전 실패 시 원본 URL 사용 (Tesseract.js가 직접 처리 시도)
            ocrSource = loadedImage;
          }
        } else {
          // 회전이 필요 없으면 URL 그대로 사용 (Tesseract.js가 직접 처리)
          // Firebase Storage URL인 경우 CORS 문제로 실패할 수 있지만 시도
          ocrSource = loadedImage;
        }
      } else {
        // Image 객체나 Blob인 경우
        let imageToRecognize = loadedImage;
        
        // 회전이 필요한 경우 Canvas로 회전
        if (rotation !== 0) {
          imageToRecognize = await rotateImage(loadedImage, rotation);
        }
        
        // 🔥 이미지 전처리 적용 (OCR 정확도 향상)
        let processedImage = imageToRecognize;
        if (imageToRecognize instanceof HTMLImageElement) {
          // Image를 Canvas로 변환 후 전처리
          const canvas = document.createElement('canvas');
          canvas.width = imageToRecognize.width || imageToRecognize.naturalWidth;
          canvas.height = imageToRecognize.height || imageToRecognize.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(imageToRecognize, 0, 0);
          
          // 전처리 적용
          const preprocessedCanvas = preprocessImage(canvas);
          processedImage = preprocessedCanvas;
        } else if (imageToRecognize instanceof HTMLCanvasElement) {
          // Canvas 전처리
          processedImage = preprocessImage(imageToRecognize);
        }
        
        // Tesseract.js는 Image, Canvas, Blob, URL 모두 지원
        // CORS 문제를 피하기 위해 Blob을 직접 전달 시도
        if (processedImage instanceof HTMLCanvasElement) {
          // Canvas를 Blob으로 변환
          ocrSource = await new Promise((resolve) => {
            processedImage.toBlob((blob) => {
              resolve(blob);
            }, 'image/png');
          });
        } else if (processedImage instanceof HTMLImageElement && imageBlob) {
          // 원본 Blob 사용 (회전이 없는 경우)
          if (rotation === 0) {
            // Blob을 Image로 변환 후 전처리
            const img = await blobToImage(imageBlob);
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const preprocessedCanvas = preprocessImage(canvas);
            ocrSource = await new Promise((resolve) => {
              preprocessedCanvas.toBlob((blob) => {
                resolve(blob);
              }, 'image/png');
            });
          } else {
            // 회전된 경우 Canvas를 Blob으로 변환
            const canvas = await rotateImage(loadedImage, rotation);
            const preprocessedCanvas = preprocessImage(canvas);
            ocrSource = await new Promise((resolve) => {
              preprocessedCanvas.toBlob((blob) => {
                resolve(blob);
              }, 'image/png');
            });
          }
        } else {
          ocrSource = processedImage;
        }
      }
      
      const { data } = await worker.recognize(ocrSource);
      const text = data.text || '';
      const confidence = data.confidence || 0;
      
      console.log(`📊 ${rotation}도 결과:`, {
        textLength: text.length,
        confidence: confidence.toFixed(2),
        preview: text.substring(0, 100)
      });
      
      results.push({
        text,
        confidence,
        rotation,
        textLength: text.trim().length
      });
    } catch (error) {
      console.warn(`⚠️ ${rotation}도 OCR 실패:`, error.message);
      results.push({
        text: '',
        confidence: 0,
        rotation,
        textLength: 0
      });
    }
  }
  
  // 🔥 개선된 결과 비교: 텍스트 길이, confidence, 그리고 텍스트 품질을 종합 고려
  const bestResult = results.reduce((best, current) => {
    // 신뢰도가 너무 낮은 결과는 제외 (30% 미만)
    if (current.confidence < 30) {
      return best;
    }
    
    // 텍스트 길이와 confidence를 가중 평균으로 계산
    const bestScore = (best.textLength * 0.4) + (best.confidence * 0.6);
    const currentScore = (current.textLength * 0.4) + (current.confidence * 0.6);
    
    // 점수가 더 높거나, 점수가 같으면 텍스트 길이가 더 긴 것 선택
    if (currentScore > bestScore) {
      return current;
    } else if (currentScore === bestScore && current.textLength > best.textLength) {
      return current;
    }
    return best;
  });
  
  // 최소 신뢰도 체크 (30% 미만이면 경고)
  if (bestResult.confidence < 30) {
    console.warn(`⚠️ OCR 신뢰도가 낮습니다: ${bestResult.confidence.toFixed(2)}%`);
  }
  
  console.log(`✅ 최적 결과 선택: ${bestResult.rotation}도 회전, 텍스트 길이: ${bestResult.textLength}자, 신뢰도: ${bestResult.confidence.toFixed(2)}%`);
  
  return bestResult;
};

/**
 * 이미지에서 OCR을 사용하여 텍스트 추출 (회전 자동 감지)
 * @param {string|Blob} imageUrlOrBlob - 이미지 URL 또는 Blob
 * @returns {Promise<string>} 추출된 텍스트
 */
export const extractTextFromImage = async (imageUrlOrBlob) => {
  let worker = null;
  try {
    console.log('🔍 OCR 시작:', imageUrlOrBlob instanceof Blob ? 'Blob' : imageUrlOrBlob);
    
    // Worker 가져오기
    worker = await getWorker();
    
    // 여러 각도로 회전하여 최적 결과 찾기
    console.log('📸 이미지 인식 시작 (회전 자동 감지)...');
    const result = await recognizeWithRotation(worker, imageUrlOrBlob);
    
    const extractedText = result.text || '';
    console.log('✅ OCR 완료');
    console.log('📊 추출된 텍스트 길이:', extractedText.length, '자');
    console.log('📊 최적 회전 각도:', result.rotation, '도');
    console.log('📊 OCR 신뢰도:', result.confidence.toFixed(2), '%');
    console.log('📄 추출된 텍스트 (처음 300자):', extractedText.substring(0, 300));
    
    if (!extractedText.trim()) {
      console.warn('⚠️ 추출된 텍스트가 비어있습니다');
    }
    
    return extractedText;
  } catch (error) {
    console.error('❌ OCR 오류:', error);
    console.error('❌ 오류 상세:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    
    // CORS 오류인 경우 이미지 객체로 재시도
    if (error.message && (error.message.includes('CORS') || error.message.includes('Failed to fetch'))) {
      console.log('🔄 CORS 오류 감지, 이미지 객체로 재시도...');
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        const imageLoadPromise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('이미지 로드 타임아웃'));
          }, 15000); // 15초 타임아웃
          
          img.onload = () => {
            clearTimeout(timeout);
            resolve(img);
          };
          img.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('이미지 로드 실패'));
          };
          img.src = imageUrl;
        });
        
        const loadedImg = await imageLoadPromise;
        
        // Worker로 이미지 객체 인식 (회전 자동 감지)
        if (!worker) {
          worker = await getWorker();
        }
        
        const result = await recognizeWithRotation(worker, loadedImg);
        console.log('✅ OCR 완료 (이미지 객체):', result.text ? result.text.substring(0, 200) : '텍스트 없음');
        return result.text || '';
      } catch (retryError) {
        console.error('❌ 재시도 실패:', retryError);
        throw new Error(`OCR 실패: ${retryError.message}`);
      }
    }
    
    throw error;
  }
};

/**
 * PDF에서 텍스트 추출 (PDF는 이미지로 변환 후 OCR 수행)
 * @param {Blob|string} pdfBlobOrUrl - PDF Blob 또는 URL
 * @returns {Promise<string>} 추출된 텍스트
 */
export const extractTextFromPDF = async (pdfBlobOrUrl) => {
  try {
    console.log('📄 PDF 처리 시작...');
    
    // PDF Blob 또는 URL에서 Blob 가져오기
    let pdfBlob;
    if (pdfBlobOrUrl instanceof Blob) {
      pdfBlob = pdfBlobOrUrl;
    } else {
      // URL인 경우 fetch로 다운로드
      const response = await fetch(pdfBlobOrUrl);
      pdfBlob = await response.blob();
    }
    
    // PDF.js 동적 import
    const pdfjsLib = await import('pdfjs-dist');
    
    // PDF.js Worker 설정
    // CDN 사용 (안정적이고 버전 호환성 보장)
    // 또는 로컬 파일 사용 시: pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'
    if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;
    }
    
    // PDF 로드
    const arrayBuffer = await pdfBlob.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    console.log(`📄 PDF 로드 완료, 총 ${pdf.numPages}페이지`);
    
    // 모든 페이지에서 텍스트 추출
    let allText = '';
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      console.log(`📄 ${pageNum}/${pdf.numPages} 페이지 처리 중...`);
      const page = await pdf.getPage(pageNum);
      
      // PDF에서 직접 텍스트 추출 시도
      try {
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        if (pageText.trim()) {
          allText += '\n' + pageText;
          console.log(`✅ ${pageNum}페이지 텍스트 추출 완료 (${pageText.length}자)`);
          continue;
        }
      } catch (textError) {
        console.log(`⚠️ ${pageNum}페이지에서 텍스트 추출 실패, OCR로 대체`);
      }
      
      // 텍스트 추출 실패 시 페이지를 이미지로 변환 후 OCR 수행
      const viewport = page.getViewport({ scale: 2.0 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      // Canvas를 Blob으로 변환
      const imageBlob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/png');
      });
      
      // OCR 수행
      const ocrText = await extractTextFromImage(imageBlob);
      if (ocrText.trim()) {
        allText += '\n' + ocrText;
        console.log(`✅ ${pageNum}페이지 OCR 완료 (${ocrText.length}자)`);
      }
    }
    
    console.log(`✅ PDF 처리 완료, 총 ${allText.length}자 추출`);
    return allText.trim();
  } catch (error) {
    console.error('❌ PDF OCR 오류:', error);
    throw error;
  }
};

/**
 * Levenshtein 거리 계산 (문자열 유사도)
 * @param {string} str1 - 첫 번째 문자열
 * @param {string} str2 - 두 번째 문자열
 * @returns {number} 편집 거리
 */
const levenshteinDistance = (str1, str2) => {
  const m = str1.length;
  const n = str2.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,     // 삭제
          dp[i][j - 1] + 1,     // 삽입
          dp[i - 1][j - 1] + 1  // 교체
        );
      }
    }
  }
  
  return dp[m][n];
};

/**
 * 문자열 유사도 계산 (0-1 사이 값)
 * @param {string} str1 - 첫 번째 문자열
 * @param {string} str2 - 두 번째 문자열
 * @returns {number} 유사도 (0-1)
 */
const stringSimilarity = (str1, str2) => {
  if (!str1 || !str2) return 0;
  const maxLen = Math.max(str1.length, str2.length);
  if (maxLen === 0) return 1;
  const distance = levenshteinDistance(str1.toLowerCase(), str2.toLowerCase());
  return 1 - (distance / maxLen);
};

/**
 * 추출된 텍스트에서 이름 추출 (개선된 버전)
 * @param {string} text - OCR로 추출된 텍스트
 * @returns {string|null} 추출된 이름
 */
export const extractName = (text) => {
  if (!text || !text.trim()) {
    console.warn('⚠️ extractName: 빈 텍스트');
    return null;
  }
  
  console.log('🔍 이름 추출 시도, 텍스트 샘플:', text.substring(0, 200));
  
  // 🔥 개선된 이름 패턴 찾기 (영문, 미얀마어, 한글 등)
  const namePatterns = [
    // 라벨이 있는 패턴 (가장 정확)
    /(?:Name|NAME|နာမည်|အမည်|Name:|NAME:)[\s:]*([A-Za-z\s\u1000-\u109F\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF]{2,50})/i,
    /(?:Full Name|FULL NAME|Full Name:)[\s:]*([A-Za-z\s\u1000-\u109F\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF]{2,50})/i,
    /(?:Given Name|GIVEN NAME)[\s:]*([A-Za-z\s\u1000-\u109F\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF]{2,50})/i,
    /(?:Surname|SURNAME|Family Name)[\s:]*([A-Za-z\s\u1000-\u109F\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF]{2,50})/i,
    // 🔥 미얀마어 이름 패턴 (우선순위 높임) - 미얀마어 문자만 포함하는 패턴
    /([\u1000-\u109F\u1100-\u11FF]+(?:\s+[\u1000-\u109F\u1100-\u11FF]+){0,4})/,
    // 영문 이름 패턴 (대문자로 시작하는 2-3단어)
    /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,2})\b/,
    // 한글 이름 패턴
    /([\uAC00-\uD7AF]{2,10})/,
  ];

  const candidates = [];
  
  for (let i = 0; i < namePatterns.length; i++) {
    const pattern = namePatterns[i];
    const matches = text.matchAll(new RegExp(pattern.source, 'gi'));
    
    for (const match of matches) {
      if (match && match[1]) {
        const name = match[1].trim();
        // 이름 유효성 검사
        if (name.length >= 2 && name.length <= 50 && !/^\d+$/.test(name)) {
          // 일반적인 단어 제외
          const commonWords = ['PASSPORT', 'PASSPORT NO', 'NRC', 'ID', 'NO', 'NAME', 'MR', 'MS', 'MRS', 'DR'];
          if (!commonWords.some(word => name.toUpperCase().includes(word))) {
            // 🔥 미얀마어 패턴 우선순위 높임 (패턴 인덱스 5는 미얀마어)
            let priority;
            if (i < 4) {
              priority = 10 - i; // 라벨이 있는 패턴 우선
            } else if (i === 4) {
              priority = 8; // 미얀마어 패턴 높은 우선순위
            } else {
              priority = 5 - (i - 5); // 나머지 패턴
            }
            
            candidates.push({
              name,
              patternIndex: i,
              priority: priority
            });
          }
        }
      }
    }
  }
  
  // 후보 중 가장 우선순위가 높은 것 선택
  if (candidates.length > 0) {
    candidates.sort((a, b) => b.priority - a.priority);
    const bestMatch = candidates[0].name;
    console.log(`✅ 이름 추출 성공:`, bestMatch);
    return bestMatch;
  }

  console.warn('⚠️ 이름 추출 실패');
  return null;
};

/**
 * 추출된 텍스트에서 NRC 번호 추출
 * @param {string} text - OCR로 추출된 텍스트
 * @returns {string|null} 추출된 NRC 번호
 */
export const extractNrcNumber = (text) => {
  if (!text || !text.trim()) {
    console.warn('⚠️ extractNrcNumber: 빈 텍스트');
    return null;
  }
  
  console.log('🔍 NRC 번호 추출 시도');
  
  // NRC 번호 패턴 (미얀마 NRC 형식: 일반적으로 숫자와 슬래시 포함)
  const nrcPatterns = [
    // 라벨이 있는 패턴 (가장 정확)
    /(?:NRC|N\.R\.C\.|NRC:|N\.R\.C\.:|နိုင်ငံသားစိစစ်ရေးကတ်)[\s:]*([0-9\/\-A-Za-z]+)/i,
    // 일반적인 NRC 형식: 숫자/문자/숫자 또는 숫자-숫자
    /([0-9]{1,3}[\/\-][A-Za-z]{1,3}[\/\-][0-9]{6})/i,
    /([0-9]{6,12}[\/\-][0-9]{6,12})/i,
    // 슬래시나 하이픈이 있는 숫자 조합
    /([0-9]{2,4}[\/\-][0-9]{2,4}[\/\-][0-9]{4,8})/,
    // 연속된 긴 숫자 (10-15자리)
    /\b([0-9]{10,15})\b/,
  ];

  for (let i = 0; i < nrcPatterns.length; i++) {
    const pattern = nrcPatterns[i];
    const matches = text.match(pattern);
    if (matches) {
      // 첫 번째 매치 그룹 찾기
      const nrc = (matches[1] || matches[0]).trim().replace(/\s+/g, '');
      if (nrc.length >= 6 && nrc.length <= 20) {
        // 숫자와 슬래시/하이픈만 포함하는지 확인
        if (/^[0-9\/\-A-Za-z]+$/.test(nrc)) {
          console.log(`✅ NRC 번호 추출 성공 (패턴 ${i + 1}):`, nrc);
          return nrc;
        }
      }
    }
  }

  console.warn('⚠️ NRC 번호 추출 실패');
  return null;
};

/**
 * 추출된 텍스트에서 여권 번호 추출
 * @param {string} text - OCR로 추출된 텍스트
 * @returns {string|null} 추출된 여권 번호
 */
export const extractPassportNumber = (text) => {
  if (!text || !text.trim()) {
    console.warn('⚠️ extractPassportNumber: 빈 텍스트');
    return null;
  }
  
  console.log('🔍 여권 번호 추출 시도');
  
  // 여권 번호 패턴 (일반적으로 영문자와 숫자 조합)
  const passportPatterns = [
    // 라벨이 있는 패턴 (가장 정확)
    /(?:Passport|PASSPORT|Passport No|PASSPORT NO|No\.|No|Number|နံပါတ်)[\s:]*([A-Z]{1,2}[0-9]{6,9})/i,
    /(?:P\/N|P\.N\.|P\/N:)[\s:]*([A-Z0-9]{6,12})/i,
    // 일반적인 여권 번호 형식: 영문 1-2자 + 숫자 6-9자
    /\b([A-Z]{1,2}[0-9]{6,9})\b/,
  ];

  // 먼저 라벨이 있는 패턴 확인
  for (let i = 0; i < passportPatterns.length; i++) {
    const pattern = passportPatterns[i];
    const match = text.match(pattern);
    if (match && match[1]) {
      const passport = match[1].trim().toUpperCase();
      if (passport.length >= 6 && passport.length <= 12) {
        // 일반적인 단어 제외
        if (!/^(NO|ID|MR|MS|MRS|DR|PASSPORT|NAME)$/i.test(passport)) {
          console.log(`✅ 여권 번호 추출 성공 (패턴 ${i + 1}):`, passport);
          return passport;
        }
      }
    }
  }

  // 라벨이 없는 경우 일반적인 여권 번호 형식 찾기
  const generalPattern = /\b([A-Z]{1,2}[0-9]{6,9})\b/g;
  const matches = text.match(generalPattern);
  if (matches) {
    for (const match of matches) {
      const passport = match.trim().toUpperCase();
      if (passport.length >= 6 && passport.length <= 12) {
        // 일반적인 단어 제외
        if (!/^(NO|ID|MR|MS|MRS|DR|PASSPORT|NAME)$/i.test(passport)) {
          console.log('✅ 여권 번호 추출 성공 (일반 패턴):', passport);
          return passport;
        }
      }
    }
  }

  console.warn('⚠️ 여권 번호 추출 실패');
  return null;
};

/**
 * 두 문자열을 비교 (대소문자 무시, 공백 제거) - 개선된 버전
 * @param {string} str1 - 첫 번째 문자열
 * @param {string} str2 - 두 번째 문자열
 * @param {number} threshold - 유사도 임계값 (0-1, 기본값: 0.9)
 * @returns {boolean} 일치 여부
 */
export const compareStrings = (str1, str2, threshold = 0.9) => {
  if (!str1 || !str2) return false;
  
  const normalize = (str) => str.toLowerCase().replace(/\s+/g, '').replace(/[\/\-]/g, '');
  const n1 = normalize(str1);
  const n2 = normalize(str2);
  
  // 정확한 일치
  if (n1 === n2) return true;
  
  // 유사도 기반 비교 (OCR 오류 허용)
  const similarity = stringSimilarity(n1, n2);
  return similarity >= threshold;
};

/**
 * 부분 일치 확인 (문자열이 다른 문자열에 포함되어 있는지) - 개선된 버전
 * @param {string} str1 - 첫 번째 문자열
 * @param {string} str2 - 두 번째 문자열
 * @param {number} minSimilarity - 최소 유사도 (0-1, 기본값: 0.7)
 * @returns {boolean} 부분 일치 여부
 */
export const partialMatch = (str1, str2, minSimilarity = 0.7) => {
  if (!str1 || !str2) return false;
  
  const normalize = (str) => str.toLowerCase().replace(/\s+/g, '').replace(/[\/\-]/g, '');
  const n1 = normalize(str1);
  const n2 = normalize(str2);
  
  // 포함 관계 확인
  if (n1.includes(n2) || n2.includes(n1)) return true;
  
  // 유사도 기반 부분 일치 (OCR 오류 허용)
  const similarity = stringSimilarity(n1, n2);
  return similarity >= minSimilarity;
};

/**
 * 백엔드 API를 통해 이미지를 Blob으로 다운로드 (CORS 우회)
 * @param {string} storagePath - Firebase Storage 경로 (예: "kyc_documents/userId/fileName")
 * @param {string} apiBaseUrl - 백엔드 API Base URL
 * @param {Function} getFirebaseToken - Firebase Auth 토큰 가져오기 함수
 * @returns {Promise<Blob>} 다운로드된 이미지 Blob
 */
const downloadImageViaProxy = async (storagePath, apiBaseUrl, getFirebaseToken) => {
  try {
    console.log('🌐 백엔드 프록시를 통해 이미지 다운로드:', storagePath);
    
    // storagePath에서 category, userId, fileName 추출
    // 예: "kyc_documents/userId/fileName" -> ["kyc_documents", "userId", "fileName"]
    const pathParts = storagePath.split('/');
    if (pathParts.length < 3) {
      throw new Error('잘못된 storagePath 형식입니다');
    }
    
    const category = pathParts[0];
    const userId = pathParts[1];
    const fileName = pathParts.slice(2).join('/'); // 파일명에 /가 포함될 수 있음
    
    // Firebase Auth 토큰 가져오기
    const token = await getFirebaseToken();
    if (!token) {
      throw new Error('Firebase Auth 토큰을 가져올 수 없습니다');
    }
    
    const baseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
    
    // ⚠️ 중요: /download 엔드포인트를 Base64 형식으로 직접 사용 (프록시 대신)
    // /download 엔드포인트가 이제 Base64를 직접 반환하므로 CORS 문제가 없음
    const encodedFileName = encodeURIComponent(fileName);
    const downloadPath = `${baseUrl}/api/files/download/${category}/${userId}/${encodedFileName}?format=base64`;
    
    console.log('📡 /download 엔드포인트 요청 정보 (Base64 형식):', {
      url: downloadPath,
      category: category,
      userId: userId,
      fileName: fileName,
      encodedFileName: encodedFileName,
      hasToken: !!token
    });
    
    // /download 엔드포인트에서 Base64 데이터 가져오기
    const downloadResponse = await fetch(downloadPath, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      },
      mode: 'cors', // 🔥 CORS 모드 명시
      credentials: 'include' // 🔥 인증 정보 포함
    });
    
    if (!downloadResponse.ok) {
      const errorText = await downloadResponse.text();
      throw new Error(`HTTP error! status: ${downloadResponse.status}, message: ${errorText}`);
    }
    
    // Base64 응답 처리
    const downloadData = await downloadResponse.json();
    console.log('📦 Base64 응답 받음:', {
      success: downloadData.success,
      hasData: !!downloadData.data,
      hasUrl: !!downloadData.url,
      contentType: downloadData.contentType,
      size: downloadData.size,
      dataType: typeof downloadData.data,
      dataLength: downloadData.data ? downloadData.data.length : 0
    });
    
    // ⚠️ 중요: data 필드가 있으면 Base64 형식으로 처리
    if (downloadData.success && downloadData.data) {
      // Base64 형식 응답 처리
      console.log('✅ Base64 형식 응답 확인, Blob 변환 시작...');
      
      // Base64 문자열을 Blob으로 변환
      const base64String = downloadData.data;
      const base64Data = base64String.includes(',') 
        ? base64String.split(',')[1] 
        : base64String;
      
      try {
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: downloadData.contentType || 'application/octet-stream' });
        
        console.log('✅ Base64를 통한 파일 다운로드 완료, 크기:', blob.size, 'bytes');
        return blob;
      } catch (base64Error) {
        console.error('❌ Base64 디코딩 실패:', base64Error);
        throw new Error('Base64 데이터 디코딩 실패: ' + base64Error.message);
      }
    } else if (downloadData.success && downloadData.url) {
      // 서명된 URL 형식 응답 (fallback - 예상치 못한 경우)
      console.warn('⚠️ 서명된 URL 형식 응답 받음 (Base64가 아님), URL 사용 시도...');
      console.warn('⚠️ 이는 백엔드가 format=base64를 무시했다는 의미입니다. 백엔드 로그를 확인하세요.');
      
      // 서명된 URL은 CORS 문제가 있으므로 실패할 가능성이 높음
      try {
        const signedUrl = downloadData.url;
        const fileResponse = await fetch(signedUrl, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit'
        });
        
        if (!fileResponse.ok) {
          throw new Error(`파일 다운로드 실패: ${fileResponse.status}`);
        }
        
        const blob = await fileResponse.blob();
        console.log('✅ 서명된 URL을 통한 파일 다운로드 완료, 크기:', blob.size, 'bytes');
        return blob;
      } catch (urlError) {
        console.error('❌ 서명된 URL 다운로드 실패 (CORS 가능):', urlError);
        throw new Error('서명된 URL 다운로드 실패 (CORS 문제 가능): ' + urlError.message);
      }
    } else {
      // 응답에 data도 url도 없는 경우
      console.error('❌ 응답에 데이터가 없음:', downloadData);
      throw new Error('Base64 데이터 또는 서명된 URL을 가져올 수 없습니다. 응답: ' + JSON.stringify(downloadData));
    }
  } catch (error) {
    console.error('❌ 백엔드 API를 통한 이미지 다운로드 실패:', error);
    throw error;
  }
};

/**
 * 파일에서 텍스트 추출 (이미지 또는 PDF)
 * @param {Object} file - 파일 객체 (url 또는 storagePath 속성 포함)
 * @param {Function} getDownloadURLFn - Firebase Storage의 getDownloadURL 함수 (선택사항)
 * @param {Object} storageRef - Firebase Storage의 ref 함수 (선택사항)
 * @param {Object} storage - Firebase Storage 인스턴스 (선택사항)
 * @param {Function} getBytesFn - Firebase Storage getBytes 함수 (선택사항)
 * @param {string} apiBaseUrl - 백엔드 API Base URL (선택사항, 프록시 사용 시 필요)
 * @param {Function} getFirebaseToken - Firebase Auth 토큰 가져오기 함수 (선택사항, 프록시 사용 시 필요)
 * @returns {Promise<string>} 추출된 텍스트
 */
export const extractTextFromFile = async (file, getDownloadURLFn = null, storageRef = null, storage = null, getBytesFn = null, apiBaseUrl = null, getFirebaseToken = null) => {
  // 🔥🔥🔥 강제 로그 - 함수 진입 확인 (빌드 최적화 방지)
  const logData = {
    hasFile: !!file,
    fileName: file?.name,
    fileType: file?.type,
    hasUrl: !!file?.url,
    hasStoragePath: !!file?.storagePath,
    urlValue: file?.url ? file.url.substring(0, 50) + '...' : 'NO URL',
    storagePathValue: file?.storagePath || 'NO STORAGE PATH',
    timestamp: new Date().toISOString()
  };
  console.log('🔥🔥🔥 extractTextFromFile 함수 진입!', logData);
  console.error('🔥🔥🔥 extractTextFromFile 함수 진입 (error로도 출력)!', logData); // error는 최적화되지 않음
  
  if (!file) {
    throw new Error('파일 객체가 필요합니다');
  }

  console.log('📄 파일 정보:', {
    name: file.name,
    type: file.type,
    url: file.url ? 'Present' : 'Missing',
    storagePath: file.storagePath ? file.storagePath : 'Missing',
    urlValue: file.url ? file.url.substring(0, 100) + '...' : 'Missing',
    storagePathValue: file.storagePath || 'Missing'
  });

  let imageUrl = file.url;
  let imageBlob = null;
  
  // ⚠️ 중요: file.url이 있으면 직접 사용 시도 (미리보기가 작동하는 것처럼)
  // Firebase Storage 서명된 URL은 CORS 문제가 있지만, img 태그나 iframe에서는 작동함
  // 하지만 fetch로는 CORS 오류가 발생할 수 있으므로, 실패 시 프록시로 대체
  console.log('🔍 file.url 체크:', {
    hasUrl: !!file.url,
    urlType: typeof file.url,
    urlValue: file.url ? file.url.substring(0, 50) : 'NO URL'
  });
  
  if (file.url) {
    try {
      console.log('🌐 file.url 직접 사용 시도 (미리보기와 동일한 방식 - Canvas 사용):', file.url.substring(0, 100));
      
      // CORS 문제를 우회하기 위해 img 태그를 사용하여 이미지를 로드한 후 Canvas로 변환
      // 이 방법은 Preview와 동일한 방식이므로 CORS 문제가 없음
      // Preview는 crossOrigin 없이도 작동하므로, 여기서도 crossOrigin 없이 시도
      if (file.type && file.type.startsWith('image/')) {
        imageBlob = await new Promise((resolve, reject) => {
          const img = new Image();
          // crossOrigin을 설정하지 않음 - Preview와 동일한 방식
          // Firebase Storage URL은 CORS를 허용하지 않을 수 있으므로, 
          // crossOrigin 없이 로드하면 tainted canvas 문제가 발생할 수 있음
          // 하지만 일단 시도해보고, 실패하면 프록시로 fallback
          
          let resolved = false;
          const timeout = setTimeout(() => {
            if (!resolved) {
              resolved = true;
              reject(new Error('이미지 로드 타임아웃'));
            }
          }, 30000); // 30초 타임아웃
          
          img.onload = () => {
            if (resolved) return;
            try {
              // Canvas를 사용하여 이미지를 Blob으로 변환
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              
              // tainted canvas 오류를 방지하기 위해 try-catch 사용
              try {
                ctx.drawImage(img, 0, 0);
              } catch (drawError) {
                clearTimeout(timeout);
                resolved = true;
                reject(new Error('Canvas drawImage 실패 (CORS): ' + drawError.message));
                return;
              }
              
              canvas.toBlob((blob) => {
                clearTimeout(timeout);
                if (resolved) return;
                resolved = true;
                if (blob) {
                  console.log('✅ Canvas를 통한 이미지 Blob 변환 완료, 크기:', blob.size, 'bytes');
                  resolve(blob);
                } else {
                  reject(new Error('Canvas toBlob 실패'));
                }
              }, file.type || 'image/jpeg', 0.95);
            } catch (error) {
              clearTimeout(timeout);
              if (!resolved) {
                resolved = true;
                reject(error);
              }
            }
          };
          
          img.onerror = (error) => {
            clearTimeout(timeout);
            if (!resolved) {
              resolved = true;
              console.warn('⚠️ 이미지 로드 실패:', error);
              reject(new Error('이미지 로드 실패'));
            }
          };
          
          // 이미지 로드 시작
          img.src = file.url;
        });
      } else {
        // 이미지가 아닌 경우 (PDF 등) fetch 시도
        const response = await fetch(file.url, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit'
        });
        if (response.ok) {
          imageBlob = await response.blob();
          console.log('✅ file.url로 직접 다운로드 완료, 크기:', imageBlob.size, 'bytes');
        } else {
          console.warn('⚠️ file.url 응답 실패:', response.status, response.statusText);
        }
      }
    } catch (urlError) {
      console.warn('⚠️ file.url 직접 사용 실패 (CORS 가능):', urlError.message);
      // CORS 오류인 경우 프록시로 대체
    }
  }
  
  // storagePath가 있고, 아직 Blob을 얻지 못한 경우 - 백엔드 프록시 시도
  if (!imageBlob && file.storagePath) {
    // 백엔드 프록시가 사용 가능한 경우 (CORS 우회)
    if (apiBaseUrl && getFirebaseToken) {
      try {
        console.log('🌐 백엔드 프록시를 통해 파일 다운로드 시도:', file.storagePath);
        imageBlob = await downloadImageViaProxy(file.storagePath, apiBaseUrl, getFirebaseToken);
        console.log('✅ 프록시를 통한 파일 다운로드 완료');
      } catch (proxyError) {
        console.warn('⚠️ 백엔드 프록시 실패, Firebase Storage SDK 시도:', proxyError.message);
        // 프록시 실패 시 Firebase Storage SDK 사용
      }
    }
    
    // 프록시가 실패했거나 사용할 수 없는 경우 Firebase Storage SDK 사용
    // getBytes를 우선 사용 (Blob으로 변환하여 CORS 문제 완전 해결)
    if (!imageBlob && storageRef && storage) {
      try {
        console.log('🔐 Firebase Storage에서 파일 다운로드:', file.storagePath);
        const fileRef = storageRef(storage, file.storagePath);
        
        // ⚠️ 근본적인 문제: Firebase Storage는 CORS를 허용하지 않으므로
        // getBytes, getDownloadURL 모두 CORS 오류가 발생합니다.
        // 프록시가 작동하지 않으므로, 프록시를 다시 시도하거나
        // 다른 방법을 사용해야 합니다.
        
        // 백엔드 API를 통해 파일 다운로드 시도 (서명된 URL 또는 프록시 사용)
        if (apiBaseUrl && getFirebaseToken && file.storagePath) {
          try {
            console.log('🔄 백엔드 API를 통해 파일 다운로드 시도...');
            imageBlob = await downloadImageViaProxy(file.storagePath, apiBaseUrl, getFirebaseToken);
            console.log('✅ 백엔드 API를 통한 파일 다운로드 성공!');
          } catch (apiError) {
            console.warn('⚠️ 백엔드 API 다운로드 실패:', apiError.message);
            // 백엔드 API 실패 시 getBytes 시도
          }
        }
        
        // 프록시가 실패한 경우 getBytes 시도 (CORS 오류가 발생할 수 있음)
        if (!imageBlob && getBytesFn) {
          try {
            console.log('📦 getBytes로 파일 다운로드 시도 (CORS 오류 가능성 있음)...');
            // 타임아웃 설정 (30초로 단축 - CORS 오류는 빠르게 발생)
            const bytesPromise = getBytesFn(fileRef);
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('getBytes 타임아웃')), 30000)
            );
            
            const bytes = await Promise.race([bytesPromise, timeoutPromise]);
            if (bytes && bytes.length > 0) {
              imageBlob = new Blob([bytes], { type: file.type || 'image/jpeg' });
              console.log('✅ getBytes로 파일 다운로드 완료, 크기:', imageBlob.size, 'bytes');
            } else {
              throw new Error('getBytes가 빈 데이터를 반환했습니다');
            }
          } catch (bytesError) {
            // CORS 오류인 경우 명시적으로 처리
            const isCorsError = bytesError.message && (
              bytesError.message.includes('CORS') || 
              bytesError.message.includes('Access-Control-Allow-Origin') ||
              bytesError.message.includes('Failed to fetch') ||
              bytesError.message.includes('ERR_FAILED') ||
              bytesError.message.includes('타임아웃')
            );
            
            if (isCorsError) {
              console.error('❌ getBytes CORS 오류 발생 (Firebase Storage CORS 설정 문제):', bytesError.message);
              console.error('❌ 근본 원인: Firebase Storage는 기본적으로 CORS를 허용하지 않습니다.');
              console.error('❌ 해결 방법: 백엔드 프록시 API가 작동해야 합니다.');
            } else {
              console.warn('⚠️ getBytes 실패:', bytesError.message);
            }
            // getBytes 실패 시 getDownloadURL 시도
            if (getDownloadURLFn) {
              try {
                console.log('🔄 getDownloadURL로 재시도...');
                imageUrl = await getDownloadURLFn(fileRef);
                console.log('✅ 인증된 URL 가져오기 완료:', imageUrl.substring(0, 100) + '...');
                // URL을 사용하되, 나중에 Blob으로 변환 시도
              } catch (urlError) {
                console.warn('⚠️ getDownloadURL도 실패:', urlError.message);
                // 마지막으로 원본 URL 사용 시도
                if (file.url) {
                  imageUrl = file.url;
                  console.log('⚠️ 원본 URL 사용 (getBytes 및 getDownloadURL 실패):', imageUrl);
                } else {
                  throw new Error('Firebase Storage에서 파일을 가져올 수 없습니다: ' + urlError.message);
                }
              }
            } else if (file.url) {
              // getDownloadURLFn이 없으면 원본 URL 사용
              imageUrl = file.url;
              console.log('⚠️ 원본 URL 사용 (getBytes 실패, getDownloadURL 없음):', imageUrl);
            } else {
              throw new Error('Firebase Storage에서 파일을 가져올 수 없습니다: ' + bytesError.message);
            }
          }
        } else if (getDownloadURLFn) {
          // getBytesFn이 없으면 getDownloadURL 사용
          try {
            console.log('🔄 getDownloadURL로 서명된 URL 가져오기...');
            imageUrl = await getDownloadURLFn(fileRef);
            console.log('✅ 인증된 URL 가져오기 완료:', imageUrl.substring(0, 100) + '...');
          } catch (urlError) {
            console.warn('⚠️ getDownloadURL 실패:', urlError.message);
            // 실패 시 원본 URL 사용
            if (file.url) {
              imageUrl = file.url;
              console.log('⚠️ 원본 URL 사용 (getDownloadURL 실패):', imageUrl);
            } else {
              throw new Error('Firebase Storage에서 파일을 가져올 수 없습니다: ' + urlError.message);
            }
          }
        } else if (file.url) {
          // 모든 방법이 실패하면 원본 URL 사용
          imageUrl = file.url;
          console.log('⚠️ 원본 URL 사용 (대체 방법 없음):', imageUrl);
        }
      } catch (error) {
        console.warn('⚠️ Firebase Storage 다운로드 실패:', error.message);
        // 실패 시 원본 URL 사용
        if (!imageUrl && file.url) {
          imageUrl = file.url;
          console.log('⚠️ 원본 URL 사용 (Firebase Storage 실패):', imageUrl);
        } else if (!imageUrl && !imageBlob) {
          throw new Error('파일을 가져올 수 없습니다: ' + error.message);
        }
      }
    } else if (!imageBlob && file.url) {
      // storagePath가 없거나 storage가 없는 경우 원본 URL 사용
      imageUrl = file.url;
      console.log('⚠️ 원본 URL 사용 (storagePath 없음):', imageUrl);
    }
  }
  
  if (!imageUrl && !imageBlob) {
    throw new Error('파일 URL 또는 Blob이 필요합니다');
  }

  const fileType = file.type || '';
  
  if (fileType.startsWith('image/')) {
    // Blob이 있으면 Blob을 직접 전달
    // URL만 있는 경우, Firebase Storage URL은 CORS 문제가 있으므로
    // Blob으로 변환 시도 (이미 시도했지만 실패한 경우 URL 사용)
    if (imageBlob) {
      return await extractTextFromImage(imageBlob);
    } else if (imageUrl) {
      // URL만 있는 경우, 먼저 Blob으로 변환 시도
      try {
        // Firebase Storage URL인 경우 CORS 문제로 직접 fetch 불가
        // Canvas를 사용하면 tainted canvas 오류 발생
        // 따라서 URL을 그대로 전달하고, Tesseract.js가 처리하도록 함
        // Tesseract.js는 내부적으로 이미지를 처리하므로 CORS 문제가 있을 수 있음
        // 하지만 일단 시도해봄
        if (imageUrl.includes('firebasestorage') || imageUrl.includes('firebasestorage.app')) {
          console.log('⚠️ Firebase Storage URL 감지, CORS 문제 가능성 있음');
          console.log('⚠️ URL을 직접 전달하여 Tesseract.js가 처리하도록 시도...');
          // URL을 직접 전달 (Tesseract.js가 내부적으로 처리 시도)
          // CORS 문제가 발생할 수 있지만, 다른 방법이 없으므로 시도
          return await extractTextFromImage(imageUrl);
        } else {
          // 다른 URL인 경우 fetch 시도
          try {
            const response = await fetch(imageUrl, {
              method: 'GET',
              mode: 'cors',
              credentials: 'omit'
            });
            if (response.ok) {
              const blob = await response.blob();
              return await extractTextFromImage(blob);
            } else {
              throw new Error(`이미지 다운로드 실패: ${response.status}`);
            }
          } catch (fetchError) {
            console.warn('⚠️ fetch 실패, URL 직접 사용:', fetchError.message);
            // fetch 실패 시 URL 직접 사용
            return await extractTextFromImage(imageUrl);
          }
        }
      } catch (urlError) {
        console.warn('⚠️ URL을 Blob으로 변환 실패, URL 직접 사용 시도:', urlError.message);
        // 변환 실패 시 URL 직접 사용 (Tesseract.js가 처리 시도)
        return await extractTextFromImage(imageUrl);
      }
    } else {
      throw new Error('이미지 URL 또는 Blob이 필요합니다');
    }
  } else if (fileType === 'application/pdf') {
    // PDF는 Blob이 있으면 Blob을 직접 전달, 없으면 URL 전달
    return await extractTextFromPDF(imageBlob || imageUrl);
  } else {
    throw new Error('지원하지 않는 파일 형식입니다');
  }
};

/**
 * 사용자 프로필 데이터와 OCR 추출 데이터 비교
 * @param {Object} userProfile - 사용자 프로필 데이터 (name, nrcNumber, passportNumber)
 * @param {Object} extractedData - OCR로 추출된 데이터 (name, nrcNumber, passportNumber)
 * @returns {Object} 비교 결과
 */
export const compareUserData = (userProfile, extractedData) => {
  const results = {
    name: {
      match: false,
      userValue: userProfile?.name || userProfile?.fullName || '',
      extractedValue: extractedData.name || '',
      confidence: 0
    },
    nrcNumber: {
      match: false,
      userValue: userProfile?.nrcNumber || '',
      extractedValue: extractedData.nrcNumber || '',
      confidence: 0
    },
    passportNumber: {
      match: false,
      userValue: userProfile?.passportNumber || '',
      extractedValue: extractedData.passportNumber || '',
      confidence: 0
    }
  };

  // 🔥 개선된 비교 로직: 유사도 기반 비교
  // 이름 비교 (부분 일치 허용, 유사도 70% 이상)
  if (results.name.userValue && results.name.extractedValue) {
    const similarity = stringSimilarity(
      results.name.userValue.toLowerCase().replace(/\s+/g, ''),
      results.name.extractedValue.toLowerCase().replace(/\s+/g, '')
    );
    results.name.match = partialMatch(results.name.userValue, results.name.extractedValue, 0.7);
    results.name.confidence = Math.round(similarity * 100);
  }

  // NRC 번호 비교 (정확한 일치 필요, 유사도 90% 이상)
  if (results.nrcNumber.userValue && results.nrcNumber.extractedValue) {
    const similarity = stringSimilarity(
      results.nrcNumber.userValue.toLowerCase().replace(/[\/\-\s]/g, ''),
      results.nrcNumber.extractedValue.toLowerCase().replace(/[\/\-\s]/g, '')
    );
    results.nrcNumber.match = compareStrings(results.nrcNumber.userValue, results.nrcNumber.extractedValue, 0.9);
    results.nrcNumber.confidence = Math.round(similarity * 100);
  }

  // 여권 번호 비교 (정확한 일치 필요, 유사도 90% 이상)
  if (results.passportNumber.userValue && results.passportNumber.extractedValue) {
    const similarity = stringSimilarity(
      results.passportNumber.userValue.toUpperCase().replace(/\s+/g, ''),
      results.passportNumber.extractedValue.toUpperCase().replace(/\s+/g, '')
    );
    results.passportNumber.match = compareStrings(results.passportNumber.userValue, results.passportNumber.extractedValue, 0.9);
    results.passportNumber.confidence = Math.round(similarity * 100);
  }

  // 전체 일치율 계산
  const totalFields = 3;
  const matchedFields = Object.values(results).filter(r => r.match).length;
  results.overallMatch = matchedFields === totalFields;
  results.matchRate = (matchedFields / totalFields) * 100;

  return results;
};

